// Patches the PWA manifest at runtime so "Add to Home Screen" uses the
// signed-in user's business name as the shortcut label, instead of the
// generic build-time "J Calculator".
//
// How it works:
// 1. Fetch the static /manifest.webmanifest emitted by vite-plugin-pwa.
// 2. Clone the JSON, override name + short_name with the business name.
// 3. Encode as a Blob URL.
// 4. Swap `<link rel="manifest">` href to the blob.
// 5. Update <meta name="apple-mobile-web-app-title"> + <title> so iOS
//    Safari (which ignores manifest) picks up the same label when adding
//    via Share → Add to Home Screen.
//
// Caveats:
// - Already-installed PWAs do NOT rename themselves. The OS only reads
//   manifest at install time. Works for new installs only.
// - short_name capped at 12 chars (Android Launcher truncation point).
//   Truncate by characters, not bytes — UTF-16 high-surrogates handled by
//   Array.from so emoji / Devanagari combining marks aren't sliced.

const MANIFEST_LINK_ID = 'app-manifest';
const APPLE_TITLE_META_NAME = 'apple-mobile-web-app-title';
const SHORT_NAME_MAX = 12;
const STATIC_MANIFEST_PATH = '/manifest.webmanifest';

let activeBlobUrl: string | null = null;
// Snapshot of the original static href set in index.html / by vite-plugin-pwa.
// Captured the first time we run so logout can restore the build-time name.
let originalManifestHref: string | null = null;

function truncate(text: string, max: number): string {
  const chars = Array.from(text);
  if (chars.length <= max) return text;
  return chars.slice(0, max).join('');
}

export async function applyDynamicManifest(businessName: string | null | undefined): Promise<void> {
  if (typeof document === 'undefined') return;
  const trimmed = (businessName ?? '').trim();
  if (trimmed.length === 0) return;

  // Locate the existing <link rel="manifest"> emitted by vite-plugin-pwa. We
  // do not create one if missing — that would mean the build is misconfigured
  // and we should not paper over it silently.
  const link = (document.querySelector('link[rel="manifest"]') ??
    document.getElementById(MANIFEST_LINK_ID)) as HTMLLinkElement | null;
  if (!link) return;
  // Snapshot the original href the first time we patch so resetDynamicManifest
  // can restore it on logout. Skip if we've already replaced it with a blob —
  // the link.href getter would return the blob URL, not the static path.
  if (originalManifestHref === null && !link.href.startsWith('blob:')) {
    originalManifestHref = link.getAttribute('href') ?? STATIC_MANIFEST_PATH;
  }

  let baseManifest: Record<string, unknown>;
  try {
    const res = await fetch(STATIC_MANIFEST_PATH, { credentials: 'omit' });
    if (!res.ok) return;
    baseManifest = (await res.json()) as Record<string, unknown>;
  } catch {
    // Manifest fetch can fail offline / behind a captive portal. Silent
    // failure preserves the static name baked into the build — acceptable.
    return;
  }

  const patched = {
    ...baseManifest,
    name: trimmed,
    short_name: truncate(trimmed, SHORT_NAME_MAX),
  };

  const blob = new Blob([JSON.stringify(patched)], { type: 'application/manifest+json' });
  // Revoke any previous URL we created so blob memory is reclaimed when the
  // user signs out and back in as a different business.
  if (activeBlobUrl) URL.revokeObjectURL(activeBlobUrl);
  activeBlobUrl = URL.createObjectURL(blob);
  link.href = activeBlobUrl;

  // iOS path: apple-mobile-web-app-title is the equivalent of short_name on
  // Android. Without it Safari falls back to <title>, which is the product
  // name, not the shop. Create the meta tag if missing.
  let appleTitle = document.querySelector(
    `meta[name="${APPLE_TITLE_META_NAME}"]`,
  ) as HTMLMetaElement | null;
  if (!appleTitle) {
    appleTitle = document.createElement('meta');
    appleTitle.setAttribute('name', APPLE_TITLE_META_NAME);
    document.head.appendChild(appleTitle);
  }
  appleTitle.setAttribute('content', truncate(trimmed, SHORT_NAME_MAX));
}

// Restore the build-time manifest + remove the iOS title meta. Called on
// logout so an install triggered between sign-out and the next sign-in
// captures the generic product name, not the previous tenant's shop name.
// Memory hygiene: revokes the blob URL so its bytes can be GC'd.
export function resetDynamicManifest(): void {
  if (typeof document === 'undefined') return;
  const link = (document.querySelector('link[rel="manifest"]') ??
    document.getElementById(MANIFEST_LINK_ID)) as HTMLLinkElement | null;
  if (link && originalManifestHref !== null) {
    link.href = originalManifestHref;
  }
  if (activeBlobUrl) {
    URL.revokeObjectURL(activeBlobUrl);
    activeBlobUrl = null;
  }
  // Remove the apple title meta we may have created so iOS Safari falls
  // back to the document <title> (build-time name) for the next install.
  const appleTitle = document.querySelector(
    `meta[name="${APPLE_TITLE_META_NAME}"]`,
  ) as HTMLMetaElement | null;
  if (appleTitle) appleTitle.parentNode?.removeChild(appleTitle);
}

// Test seam — exposed only so the unit test can reset module-level state
// between cases. Production code should never call this.
export function _resetForTest(): void {
  if (activeBlobUrl) URL.revokeObjectURL(activeBlobUrl);
  activeBlobUrl = null;
  originalManifestHref = null;
}

export const _internal = { truncate };
